import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useSupportSummary } from '../../hooks/useSupportSummary';
import { BrandLogo } from '../BrandLogo';
import { Dropdown } from '../Dropdown';

/**
 * Port of the <nav id="sidebar"> block in Pages/Shared/_Layout.cshtml, including the
 * role-conditional Administration section.
 */
interface SidebarProps {
  show: boolean;
  onNavigate: () => void;
}

interface NavItemProps {
  to: string;
  icon: string;
  label: string;
  /** Marks the link active for any child route, matching page.StartsWith(...) in Razor. */
  matchPrefix?: string | string[];
  /** Optional badge at the right end of the link. */
  badge?: ReactNode;
  onNavigate: () => void;
}

/**
 * The badge next to My Company for a Suspended or Deactivated account - same icon, colour and
 * tooltip as ClientStatusService.SidebarBadge in Core.
 */
function statusBadge(status: string | null | undefined): ReactNode {
  const b =
    status === 'Suspended'
      ? { icon: 'bi-pause-circle-fill', css: 'bg-warning text-dark', title: 'Account suspended - live API keys are refused' }
      : status === 'Deactivated'
        ? { icon: 'bi-slash-circle-fill', css: 'bg-danger', title: 'Account deactivated - all API calls are refused' }
        : null;
  if (!b) return null;
  return (
    <span className={`badge rounded-pill ${b.css} ms-auto sidebar-status-badge`} title={b.title}>
      <i className={`bi ${b.icon}`} />
      <span className="visually-hidden">{b.title}</span>
    </span>
  );
}

/** A count at the right end of a link, e.g. support replies not yet read. Nothing when zero. */
function countBadge(count: number | undefined, title: string): ReactNode {
  if (!count) return null;
  return (
    <span className="badge rounded-pill bg-primary ms-auto" title={title}>
      {count}
      <span className="visually-hidden"> {title}</span>
    </span>
  );
}

function NavItem({ to, icon, label, matchPrefix, badge, onNavigate }: NavItemProps) {
  const location = useLocation();
  const active = matchPrefix
    ? [matchPrefix].flat().some((p) => location.pathname.startsWith(p))
    : location.pathname === to;

  // A plain Link, not a NavLink, on purpose.
  //
  // NavLink adds its own "active" class whenever the current URL matches, and without `end` that
  // includes descendant routes — so /fraud/alerts lit up both "Fraud Alerts" and "Fraud Analytics".
  // The `active` computed above was already correct; the bug was having two things decide, one of
  // which this component cannot see. Link has no opinion, so the rule above is the only one.
  //
  // aria-current is what NavLink was contributing that is worth keeping.
  return (
    <Link
      to={to}
      className={`nav-link ${active ? 'active' : ''}`}
      aria-current={active ? 'page' : undefined}
      onClick={onNavigate}
    >
      <i className={`bi ${icon}`} /> {label}
      {badge}
    </Link>
  );
}

export function Sidebar({ show, onNavigate }: SidebarProps) {
  const { user, signOut } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const support = useSupportSummary(!!user);

  return (
    <nav id="sidebar" className={`sidebar ${show ? 'show' : ''}`}>
      <div className="sidebar-brand">
        <Link to="/dashboard" className="text-decoration-none" onClick={onNavigate} aria-label="InertiaID dashboard">
          <BrandLogo size={32} onDark />
        </Link>
      </div>

      <div className="sidebar-nav">
        {/*
          Grouped by task since 2026-10-23 (was Main / Security / Reports / Administration). Same
          groups, order and labels as Pages/Shared/_Layout.cshtml; keep the two the same.
        */}
        <div className="nav-section-label">Main</div>
        <NavItem to="/dashboard" icon="bi-grid-1x2-fill" label="Dashboard" onNavigate={onNavigate} />
        <NavItem
          to="/verifications"
          icon="bi-person-check-fill"
          label="Verifications"
          matchPrefix="/verifications"
          onNavigate={onNavigate}
        />
        {/* Documents (/documents) is hidden from the menu for now (2026-10-29): not in use. The route
            and page are kept; to bring it back, restore:
            <NavItem to="/documents" icon="bi-file-earmark-person-fill" label="Documents"
                     matchPrefix="/documents" onNavigate={onNavigate} /> */}

        <div className="nav-section-label">Insights</div>
        <NavItem to="/reports/analytics" icon="bi-bar-chart-line-fill" label="Analytics" onNavigate={onNavigate} />
        <NavItem
          to="/fraud"
          icon="bi-exclamation-triangle-fill"
          label="Fraud Analytics"
          onNavigate={onNavigate}
        />
        <NavItem
          to="/fraud/alerts"
          icon="bi-bell-fill"
          label="Fraud Alerts"
          matchPrefix="/fraud/alerts"
          onNavigate={onNavigate}
        />
        <NavItem
          to="/reports/compliance"
          icon="bi-clipboard2-check-fill"
          label="Compliance"
          onNavigate={onNavigate}
        />

        <div className="nav-section-label">Developers</div>
        {!isSuperAdmin && (
          <>
            <NavItem to="/account/api-keys" icon="bi-key-fill" label="API Keys" onNavigate={onNavigate} />
            <NavItem
              to="/account/integration"
              icon="bi-plug-fill"
              label="Webhooks & Redirect"
              onNavigate={onNavigate}
            />
          </>
        )}
        <NavItem
          to="/api-logs"
          icon="bi-activity"
          label="API Logs"
          matchPrefix="/api-logs"
          onNavigate={onNavigate}
        />
        <NavItem to="/demo" icon="bi-lightning-charge-fill" label="Demo" onNavigate={onNavigate} />
        {/* The docs have their own layout, so they open in a new tab rather than replacing the app. */}
        <a className="nav-link" href="/docs" target="_blank" rel="noreferrer" onClick={onNavigate}>
          <i className="bi bi-book-fill" /> Docs
          <i className="bi bi-box-arrow-up-right ms-auto small opacity-50" />
        </a>

        {isSuperAdmin ? (
          <>
            <div className="nav-section-label">Platform</div>
            <NavItem
              to="/clients"
              icon="bi-building"
              label="Clients"
              matchPrefix="/clients"
              onNavigate={onNavigate}
            />
            <NavItem to="/users" icon="bi-people-fill" label="Users" matchPrefix="/users" onNavigate={onNavigate} />
            <NavItem
              to="/admin/plans"
              icon="bi-credit-card-fill"
              label="Plans"
              matchPrefix="/admin/plans"
              onNavigate={onNavigate}
            />
            <NavItem
              to="/admin/support"
              icon="bi-headset"
              label="Support Inbox"
              matchPrefix="/admin/support"
              badge={countBadge(support?.unread, 'requests awaiting the team')}
              onNavigate={onNavigate}
            />
            <NavItem to="/admin/settings" icon="bi-gear-fill" label="Platform Settings" onNavigate={onNavigate} />
            <NavItem
              to="/audit-logs"
              icon="bi-journal-text"
              label="Audit Logs"
              matchPrefix="/audit-logs"
              onNavigate={onNavigate}
            />
          </>
        ) : (
          <>
            <div className="nav-section-label">Settings</div>
            <NavItem
              to="/account/company"
              icon="bi-building"
              label="Company"
              badge={statusBadge(user?.clientStatus)}
              onNavigate={onNavigate}
            />
            <NavItem
              to="/account/verification-rules"
              icon="bi-sliders"
              label="Verification Rules"
              onNavigate={onNavigate}
            />
            <NavItem
              to="/account/fraud-rules"
              icon="bi-shield-exclamation"
              label="Fraud Rules"
              onNavigate={onNavigate}
            />
            <NavItem to="/account/branding" icon="bi-palette" label="Branding" onNavigate={onNavigate} />
            <NavItem
              to="/account/credits"
              icon="bi-wallet2"
              label="Billing"
              matchPrefix={['/account/credits', '/account/billing']}
              onNavigate={onNavigate}
            />
            <NavItem to="/users" icon="bi-people-fill" label="Team" matchPrefix="/users" onNavigate={onNavigate} />
            <NavItem to="/account/notifications" icon="bi-bell" label="Notifications" onNavigate={onNavigate} />
            <NavItem
              to="/audit-logs"
              icon="bi-journal-text"
              label="Audit Log"
              matchPrefix="/audit-logs"
              onNavigate={onNavigate}
            />

            <div className="nav-section-label">Help</div>
            <NavItem
              to="/support"
              icon="bi-headset"
              label="Support"
              matchPrefix="/support"
              badge={countBadge(support?.unread, 'unread replies from the team')}
              onNavigate={onNavigate}
            />
          </>
        )}
      </div>

      {/*
        The footer used to be a name, a role and a sign-out button — no way to reach your own
        account at all. Anyone wanting to change their password had to guess it lived on the Users
        page, find their own row and open it, which is not a route anybody finds. It is now a menu.
      */}
      <div className="sidebar-footer">
        <div className="d-flex align-items-center justify-content-between gap-2">
          {/* dropup: .sidebar clips its overflow, so a menu opening downward from the footer
              would be cut off. Upward keeps it on screen, over the nav. */}
          <Dropdown
            align="start"
            containerClassName="dropup"
            toggleClassName="btn btn-link p-0 border-0 text-start d-flex align-items-center gap-2 dropdown-toggle"
            menuClassName="shadow"
            toggle={
              <>
                <div
                  className="rounded-circle bg-primary d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: '32px', height: '32px' }}
                >
                  <i className="bi bi-person-fill text-white" style={{ fontSize: '0.85rem' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    className="text-white text-truncate"
                    style={{ fontSize: '0.8rem', fontWeight: 600 }}
                  >
                    {user?.fullName}
                  </div>
                  <div style={{ fontSize: '0.7rem', opacity: 0.6, color: '#fff' }}>
                    {user?.roleDisplayName}
                  </div>
                </div>
              </>
            }
          >
            {(close) => (
              <>
                <li>
                  <Link
                    className="dropdown-item"
                    to="/account/profile"
                    onClick={() => {
                      close();
                      onNavigate();
                    }}
                  >
                    <i className="bi bi-person-circle me-2" />
                    My profile
                  </Link>
                </li>
                {!isSuperAdmin && (
                  <li>
                    <Link
                      className="dropdown-item"
                      to="/support/new"
                      onClick={() => {
                        close();
                        onNavigate();
                      }}
                    >
                      <i className="bi bi-headset me-2" />
                      Contact support
                    </Link>
                  </li>
                )}
                <li>
                  <hr className="dropdown-divider" />
                </li>
                <li>
                  <button
                    type="button"
                    className="dropdown-item text-danger"
                    onClick={() => {
                      close();
                      void signOut();
                    }}
                  >
                    <i className="bi bi-box-arrow-right me-2" />
                    Sign out
                  </button>
                </li>
              </>
            )}
          </Dropdown>

          {/* Kept alongside the menu: signing out is frequent enough to deserve one click. */}
          <button
            type="button"
            className="btn btn-link p-0 border-0 flex-shrink-0"
            title="Sign out"
            style={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1 }}
            onClick={() => void signOut()}
          >
            <i className="bi bi-box-arrow-right" style={{ fontSize: '1.1rem' }} />
          </button>
        </div>
      </div>
    </nav>
  );
}
